import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  clearImpersonation,
  getImpersonation,
  type ImpersonationEnd,
  parseImpersonationHash,
  setImpersonation,
} from '@/lib/impersonation';
import { useSession } from '@/stores/session';

const ENDS: readonly ImpersonationEnd[] = ['expired', 'ended', 'invalid', 'exited'];

/**
 * Landing page for CoreUp staff opened from the CoreUp back office ("view as shop"):
 * `/impersonate#token=…&tenant=…[&branch=…]`. The token is moved from the URL fragment into sessionStorage and
 * removed from the address bar immediately; the shop (and branch, when chosen) is selected and the app opens
 * without asking for the shop's own credentials. `?ended=expired|ended|invalid|exited` is the notice shown when
 * such a session is over.
 */
export const Route = createFileRoute('/impersonate')({
  validateSearch: (s: Record<string, unknown>): { ended?: ImpersonationEnd } => ({
    ended: ENDS.find((e) => e === s.ended),
  }),
  component: ImpersonatePage,
});

function ImpersonatePage() {
  const { ended } = Route.useSearch();
  return ended ? <EndedNotice ended={ended} /> : <Landing />;
}

function Landing() {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const landing = parseImpersonationHash(window.location.hash);
    window.history.replaceState(null, '', window.location.pathname);
    if (!landing) {
      // React StrictMode runs effects twice in dev: the 2nd run finds the fragment already stripped by the 1st.
      if (getImpersonation()) {
        void navigate({ to: '/', replace: true });
        return;
      }
      clearImpersonation();
      setInvalid(true);
      return;
    }
    setImpersonation({ token: landing.token, tenantId: landing.tenantId });
    const session = useSession.getState();
    session.reset();
    session.setTenant(landing.tenantId);
    session.setBranch(landing.branchId);
    qc.clear();
    void navigate({ to: '/', replace: true });
  }, [navigate, qc]);

  return invalid ? (
    <EndedNotice ended="invalid" />
  ) : (
    <div
      className="grid min-h-dvh place-items-center p-6 text-center text-sm text-muted-foreground"
      role="status"
    >
      {t('impersonation.starting')}
    </div>
  );
}

function EndedNotice({ ended }: { ended: ImpersonationEnd }) {
  const { t } = useTranslation('nav');
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="grid max-w-md gap-4 text-center" role="alert">
        <h1 className="text-lg font-semibold">{t(`impersonation.end.${ended}.title`)}</h1>
        <p className="text-sm text-muted-foreground">{t(`impersonation.end.${ended}.body`)}</p>
        <Button asChild variant="outline" className="justify-self-center">
          <Link to="/sign-in" search={{}}>
            {t('impersonation.toSignIn')}
          </Link>
        </Button>
      </div>
    </div>
  );
}
